import { useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { useReveal } from "../hooks/useReveal";
import { Mail, Phone, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "../i18n/translations";

const FORMSUBMIT_ENDPOINT = "https://formsubmit.co/architectorial.studio@gmail.com";

const Contact = () => {
  const { t } = useLanguage();
  const infoRef = useReveal();
  const formRef = useReveal();

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const formElement = e.currentTarget;
    if (!formElement.checkValidity()) {
      formElement.reportValidity();
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("phone", form.phone);
      formData.append("message", form.message);
      formData.append("_honey", form.website);
      formData.append("_captcha", "false");
      formData.append("_template", "table");
      formData.append("_subject", "New contact request from Architectorial");

      const res = await fetch(FORMSUBMIT_ENDPOINT, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });

      if (res.ok) {
        toast.success(t.contact.success, { duration: 5000 });
        setForm({ name: "", email: "", phone: "", message: "", website: "" });
      } else {
        toast.error(t.contact.error, { duration: 5000 });
      }
    } catch {
      toast.error(t.contact.error, { duration: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-white py-20 md:py-32 lg:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-12 lg:gap-20">
          {/* Info */}
          <div ref={infoRef} className="reveal lg:col-span-5">
            <p className="text-[11px] md:text-sm uppercase tracking-[0.28em] font-semibold text-[#52525B] mb-5 md:mb-6">
              — {t.contact.overline}
            </p>
            <h2 className="font-serif text-[2.5rem] sm:text-5xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#1A1A1A] leading-[1.05] mb-5 md:mb-6">
              {t.contact.title}
            </h2>
            <p className="text-base md:text-lg text-[#52525B] leading-relaxed mb-10 md:mb-12 max-w-md">
              {t.contact.subtitle}
            </p>

            <div className="space-y-6 max-w-md">
              <div className="pt-6 border-t border-[#E5E5E5]">
                <div className="font-serif text-2xl text-[#1A1A1A] mb-1">
                  Oriol López
                </div>
                <div className="text-sm text-[#52525B] uppercase tracking-[0.2em]">
                  {t.contact.info.role}
                </div>
              </div>

              <ul className="space-y-5 pt-2">
                <li className="flex items-start gap-4">
                  <Mail strokeWidth={1.25} className="w-5 h-5 text-[#52525B] mt-1 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 mb-1">
                      {t.contact.info.emailLabel}
                    </div>
                    <a
                      data-testid="contact-email-link"
                      href={CONTACT.emailHref}
                      className="text-[#1A1A1A] hover:text-[#52525B] transition-colors link-underline"
                    >
                      {CONTACT.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Phone strokeWidth={1.25} className="w-5 h-5 text-[#52525B] mt-1 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 mb-1">
                      {t.contact.info.phoneLabel}
                    </div>
                    <a
                      data-testid="contact-phone-link"
                      href={CONTACT.phoneHref}
                      className="text-[#1A1A1A] hover:text-[#52525B] transition-colors link-underline"
                    >
                      {CONTACT.phone}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin strokeWidth={1.25} className="w-5 h-5 text-[#52525B] mt-1 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 mb-1">
                      {t.contact.info.locationLabel}
                    </div>
                    <div className="text-[#1A1A1A]">{t.contact.info.location}</div>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Form */}
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="reveal lg:col-span-7 bg-[#F8F8F7] p-6 sm:p-8 md:p-12 lg:p-14"
            noValidate
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              <div className="md:col-span-1">
                <label
                  htmlFor="name"
                  className="text-[10px] uppercase tracking-[0.25em] text-[#52525B] font-semibold"
                >
                  {t.contact.name}
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={handleChange}
                  data-testid="contact-input-name"
                  className="input-line text-[1.15rem] sm:text-base"
                  autoComplete="name"
                />
              </div>
              <div className="md:col-span-1">
                <label
                  htmlFor="email"
                  className="text-[10px] uppercase tracking-[0.25em] text-[#52525B] font-semibold"
                >
                  {t.contact.email}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  data-testid="contact-input-email"
                  className="input-line text-[1.15rem] sm:text-base"
                  autoComplete="email"
                />
              </div>
              <div className="md:col-span-2">
                <label
                  htmlFor="phone"
                  className="text-[10px] uppercase tracking-[0.25em] text-[#52525B] font-semibold"
                >
                  {t.contact.phone}
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  data-testid="contact-input-phone"
                  className="input-line text-[1.15rem] sm:text-base"
                  autoComplete="tel"
                />
              </div>
              <div className="md:col-span-2 mt-2">
                <label
                  htmlFor="message"
                  className="text-[10px] uppercase tracking-[0.25em] text-[#52525B] font-semibold"
                >
                  {t.contact.message}
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  required
                  value={form.message}
                  onChange={handleChange}
                  data-testid="contact-input-message"
                  className="input-line text-[1.15rem] sm:text-base resize-none"
                />
              </div>
            </div>

            {/* Honeypot — hidden from humans, visible to naive bots */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-9999px",
                width: 1,
                height: 1,
                overflow: "hidden",
                opacity: 0,
              }}
            >
              <label htmlFor="website">Website</label>
              <input
                id="website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={handleChange}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="group mt-10 inline-flex w-full sm:w-auto items-center justify-center sm:justify-start gap-3 bg-[#1A1A1A] hover:bg-[#3F3F46] disabled:opacity-60 disabled:cursor-not-allowed text-white px-7 py-[1.15rem] sm:px-8 sm:py-4 text-[1rem] sm:text-sm font-semibold tracking-wide uppercase transition-all duration-300 hover:-translate-y-0.5"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> {t.contact.sending}
                </>
              ) : (
                <>
                  {t.contact.send}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
